import { Pause, PlayArrow } from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { Explicitness } from '../Explicitness';
import { LazyThumbnail } from '../LazyThumbnail';
import { TruncatedText } from '../CollectionCard/TruncatedText';
import { formatDate, isRecentlyAdded } from './format';
import * as styles from './TrackClipTable.styles';
import type { ColumnKey, TrackWithClips } from './types';

export interface TrackRowProps {
  track: TrackWithClips;
  columns: ColumnKey[];
  onMoreClick: () => void;
  onPlay?: () => void;
  isPlaying?: boolean;
}

export const TrackRow: React.FC<TrackRowProps> = ({
  track,
  columns,
  onMoreClick,
  onPlay,
  isPlaying = false,
}) => {
  const cellFor = (key: ColumnKey): React.ReactNode => {
    switch (key) {
      case 'thumbnail':
        return (
          <Box sx={styles.thumbnailContainer}>
            <LazyThumbnail
              src={track.artworkThumbnailUrl}
              fallbackSrc={track.artworkPreSignedUrl}
              alt="Album cover"
              className="track-artwork"
              placeholder={
                <Typography variant="caption" color="text.secondary">
                  No Art
                </Typography>
              }
            />
            {onPlay && (
              <IconButton
                className="play-button"
                size="small"
                onClick={onPlay}
                sx={isPlaying ? styles.playButtonActive : styles.playButton}
                aria-label={isPlaying ? 'Pause clip' : 'Play clip'}
              >
                {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
              </IconButton>
            )}
          </Box>
        );
      case 'titleArtist':
        return (
          <Box>
            {isRecentlyAdded(track.dateAddedMs) && (
              <Typography variant="overline" sx={styles.newLabel}>
                New
              </Typography>
            )}
            <TruncatedText
              text={track.trackTitle}
              variant="body1"
              color="text.primary"
              sx={styles.titleArtistText}
            />
            <TruncatedText
              text={track.artist}
              variant="body2"
              color="text.secondary"
              sx={styles.titleArtistText}
            />
          </Box>
        );
      case 'album':
        return (
          <TruncatedText text={track.album} variant="body2" sx={styles.albumText} />
        );
      case 'explicitness':
        return track.explicitness ? (
          <Explicitness explicitness={track.explicitness} />
        ) : null;
      case 'dateAdded':
        return (
          <Typography variant="body2" sx={styles.numericCell}>
            {formatDate(track.dateAddedMs)}
          </Typography>
        );
      case 'language':
        return track.language ? (
          <Typography variant="body2" sx={styles.numericCell}>
            {track.language}
          </Typography>
        ) : null;
      case 'popularity':
        return track.popularity != null ? (
          <Typography variant="body2" sx={styles.numericCell}>
            {track.popularity}
          </Typography>
        ) : null;
      case 'more': {
        const customClipCount = track.customClips.length;
        const moreButton = (
          <Button
            size="small"
            variant="outlined"
            onClick={onMoreClick}
            aria-label={`Clip options for ${track.trackTitle}`}
          >
            Clip Options
          </Button>
        );
        // Themed count badge on the Clip Options button so tracks with custom
        // clips are spottable at a glance; hidden entirely when a track has none.
        return (
          <Box sx={styles.actionsCell}>
            {customClipCount > 0 ? (
              <Tooltip
                title={`${customClipCount} custom clip${customClipCount === 1 ? '' : 's'}`}
                placement="top"
                arrow
              >
                <Badge
                  badgeContent={customClipCount}
                  color="primary"
                  overlap="rectangular"
                  aria-label={`${customClipCount} custom clips`}
                >
                  {moreButton}
                </Badge>
              </Tooltip>
            ) : (
              moreButton
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    <TableRow hover sx={styles.curatedRow}>
      {columns.map((key) => (
        <TableCell key={key}>{cellFor(key)}</TableCell>
      ))}
    </TableRow>
  );
};
